<template>
  <div
    class="rt-time-picker"
    @mouseenter="inputHovering = true"
    @mouseleave="inputHovering = false"
  >
    <el-time-select
      v-if="computedPickerOptions === 'selectPicker'"
      ref="timeSelect"
      :value="computedSelectValue"
      :disabled="computedDisabled"
      :readonly="readonly"
      :placeholder="computedSelectPlaceholder"
      :picker-options="pickerOptions"
      v-bind="$attrs"
      @input="_selectHandler"
    >
      <div
        v-if="$slots['header']"
        slot="time-header"
      >
        <slot name="header"></slot>
      </div>
      <div
        v-if="$slots['footer']"
        slot="time-footer"
      >
        <slot name="footer"></slot>
      </div>
    </el-time-select>
    <el-time-picker
      v-else-if="computedPickerOptions === 'timePicker'"
      ref="timePicker"
      :value="inputValue"
      :disabled="computedDisabled"
      :readonly="readonly"
      :placeholder="computedSelectPlaceholder"
      :start-placeholder="computedStartPlaceholder"
      :end-placeholder="computedEndPlaceholder"
      :picker-options="pickerOptions"
      :format="computedFormat"
      :now="now"
      :stepper="stepper"
      :click-now-show-picker="clickNowShowPicker"
      v-bind="$attrs"
      @input="inputHandler"
    >
      <div
        v-if="$slots['header']"
        slot="time-header"
      >
        <slot name="header"></slot>
      </div>
      <div
        v-if="$slots['footer']"
        slot="time-footer"
      >
        <slot name="footer"></slot>
      </div>
    </el-time-picker>

    <rt-icon
      v-show="!computedDisabled && computedSuffixIcon"
      name="rt-base-circle-time-lined"
      @click="getTimeFocus"
    />
    <rt-icon
      v-if="showClose"
      :name="computedIconName"
      :style="computedClearIconStyle"
      @click="clear"
    />
  </div>
</template>

<script>
import moment from 'moment';

import inputFocusMixins from './inputFocusMixins.js';
import RtIcon from '../icon/rtIcon.vue';
import ElTimePicker from 'vue-xpc/src/element-ui/packages/date-picker/picker/elTimePicker.vue';
import ElTimeSelect from 'vue-xpc/src/element-ui/packages/date-picker/picker/elTimeSelect.vue';
import xpcConfig from 'vue-xpc/src/config';
import Placeholder from './placeholder.js';

export default {
  name: 'RtTimePicker',
  components: { RtIcon, ElTimePicker, ElTimeSelect },
  mixins: [inputFocusMixins, Placeholder],
  props: {
    value: [String, Date, Array],
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    startPlaceholder: String,
    endPlaceholder: String,
    // 配置项
    pickerOptions: {
      type: Object,
      default: function () {
        return { format: 'HH:mm' };
      }
    },
    // 输入框时间格式
    format: {
      type: String,
      default: 'HH:mm'
    },
    // 是否展示此刻
    now: Boolean,
    // 自定义清空按钮图标
    clearIconName: {
      type: String,
      default: undefined
    },
    // 自定义清空按钮位置
    clearIconRight: {
      type: Number,
      default: 0
    },
    // 分钟 / 秒跨度
    stepper: [Number, Boolean],
    // 输出value的时间格式
    valueFormat: {
      type: String,
      default: null
    },
    // 点击 <此刻> 不关闭 picker
    clickNowShowPicker: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      inputValue: this.value || null,
      selectValue: this.value || null,
      type: '',
      inputHovering: false
    };
  },
  inject: {
    elForm: {
      default: ''
    }
  },
  provide () {
    return {
      rtTimePicker: this
    }
  },
  computed: {
    /**
     * 当清空按钮设置偏移量的时候，显示后缀图标
     */
    computedSuffixIcon () {
      return (
        (this.clearIconRight || xpcConfig.get('clearicon.clearIconRight')) >=
          24 || !this.showClose
      );
    },
    computedIconName () {
      return this.clearIconName || xpcConfig.get('clearicon.clearIconName');
    },
    computedClearIconStyle () {
      let style = {};
      style['marginRight'] =
        this.clearIconRight || xpcConfig.get('clearicon.clearIconRight') + 'px';
      return style;
    },
    computedFormat () {
      return this.pickerOptions['format'] === 'A' ? '' : this.format;
    },
    computedSelectValue () {
      return this.selectValue ? moment(this.selectValue).format('HH:mm') : null;
    },
    computedPickerOptions () {
      return this.pickerOptions['start'] ||
        this.pickerOptions['end'] ||
        this.pickerOptions['step']
        ? 'selectPicker'
        : 'timePicker';
    },
    computedDisabled () {
      return this.disabled || (this.elForm || {}).disabled;
    },
    showClose () {
      if (this.computedPickerOptions === 'selectPicker') {
        return !this.computedDisabled && this.computedSelectValue && this.inputHovering;
      }
      return !this.computedDisabled && this.inputValue && this.inputHovering;
    }
  },

  methods: {
    /**
     * @private
     */
    _selectHandler (newValue) {
      let stringTime = moment().format('YYYY-MM-DD') + ' ' + newValue;
      let timestamp = stringTime.replace(/-/g, '/'); // 为了兼容IOS，需先将字符串转换为'2018/9/11 9:11:23'
      timestamp = Date.parse(timestamp);
      this.selectValue = new Date(timestamp);
      const value = this.valueFormat
        ? moment(this.selectValue).format(this.valueFormat)
        : this.selectValue;
      this.$emit('input', value);
      this.$emit('change', value);
    },
    inputHandler (newValue) {
      this.inputValue = newValue;
      const value = this.valueFormat
        ? moment(newValue).format(this.valueFormat)
        : newValue;
      this.$emit('input', value);
      this.$emit('change', value);
    },
    clear () {
      if (this.computedPickerOptions === 'selectPicker') {
        this.selectValue = null;
        this.$emit('input', this.selectValue);
        this.$emit('change', this.selectValue);
      } else {
        this.$refs.timePicker.emitInput(null);
        this.$refs.timePicker.emitChange(null);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// rtTimePicker.vue?7fefcd26